/**
 * IframePage - Page d'intégration iframe Climadiag
 * Version moderne avec Vite + React 19 + Material-UI v7
 * Migration de front/src/pages/iframe.js
 */
import React, { useEffect } from 'react';
import { Box, Container, Paper, Skeleton, Stack } from '@mui/material';

import setupInteractiveIframe from '../utils/interactiveIframe';

/**
 * Configuration par défaut de l'iframe
 */
const IFRAME_CONFIG = {
  // URL de base de l'application Climadiag (à configurer selon l'environnement)
  basePath: import.meta.env.VITE_IFRAME_BASE_PATH || 'https://localhost:3000',
  iframeSelector: 'iframe#iframe',
  verticalPaddingSelectors: {
    top: 'main > header.MuiBox-root', // Header sticky
    // bottom: 'footer', // Footer sticky si nécessaire
  },
};

/**
 * Hook custom pour gérer l'effet iframe
 * Initialise la communication interactive avec l'iframe
 */
const useInteractiveIframeEffect = () => {
  useEffect(() => {
    setupInteractiveIframe(IFRAME_CONFIG);
  }, []);
};

/**
 * Composant Header simulé pour l'intégration sur meteofrance.com
 * Reproduit l'apparence du header Météo France avec logos et navigation
 * @returns {JSX.Element} Header avec logos et navigation simulée
 */
const MeteoFranceHeader = () => (
  <Box
    component="header"
    sx={{
      position: 'fixed',
      top: 0,
      width: '100%',
      height: '180px',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'white',
      zIndex: 1000,
    }}
  >
    <Container sx={{ pt: 1.5 }}>
      {/* Logos Marianne et Météo France */}
      <Stack direction="row" spacing={2}>
        <Box
          component="img"
          src="https://meteofrance.com/themes/custom/theme_public/dist/img/marianne.svg"
          alt="Logo République Française"
          sx={{ height: '80px', width: 'auto' }}
        />
        <Box
          component="img"
          src="https://meteofrance.com/sites/meteofrance.com/files/logo/LOGO_MF.png"
          alt="Logo Météo France"
          sx={{ height: '80px', width: '80px' }}
        />
      </Stack>

      {/* Simulated navigation with skeletons */}
      <Stack direction="row" spacing={5} sx={{ mt: 2 }} justifyContent="space-between">
        <Skeleton variant="text" animation={false} sx={{ fontSize: '1em', width: 90 }} />
        <Skeleton variant="text" animation={false} sx={{ fontSize: '1em', width: 110 }} />
        <Skeleton variant="text" animation={false} sx={{ fontSize: '1em', width: 130 }} />
        <Skeleton variant="text" animation={false} sx={{ fontSize: '1em', width: 80 }} />
        <Skeleton variant="text" animation={false} sx={{ fontSize: '1em', width: 130 }} />
        <Skeleton variant="text" animation={false} sx={{ fontSize: '1em', width: 100 }} />
        <Skeleton
          variant="text"
          animation={false}
          sx={{ fontSize: '1em', width: 20, bgcolor: 'primary.main', opacity: 0.2 }}
        />
      </Stack>
    </Container>

    {/* Blue sub-navigation */}
    <Box
      sx={{
        height: '36px',
        background: '#036ba1',
        marginTop: 'auto',
      }}
    >
      <Container>
        <Skeleton
          variant="text"
          animation={false}
          sx={{ fontSize: '1em', width: 150, bgcolor: 'rgba(250 250 250 / 0.2)', mt: 0.8 }}
        />
      </Container>
    </Box>
  </Box>
);

/**
 * Composant Iframe principal
 * Affiche l'application Climadiag dans un iframe responsive
 * @returns {JSX.Element} Iframe de l'application
 */
const ClimadiagIframe = () => (
  <Box
    component="iframe"
    id="iframe"
    src={IFRAME_CONFIG.basePath}
    title="Application Climadiag Commune"
    sx={{
      mx: 'auto',
      display: 'block',
      width: '90%',
      height: '800px',
      border: 0,
      borderRadius: 1,
    }}
  />
);

/**
 * Contenu additionnelcompnnel simulé sous l'iframe
 * Reproduit le contenu Météo France avec skeletons
 * @returns {JSX.Element} Contenu simulé
 */
const AdditionalContent = () => (
  <Container sx={{ pb: 10 }}>
    <Paper
      elevation={6}
      sx={{
        mt: 10,
        p: 3,
        borderRadius: 3,
      }}
    >
      <Skeleton
        animation={false}
        sx={{
          width: 200,
          bgcolor: 'secondary.main',
          fontSize: '1.5em',
          opacity: 0.2,
          m: 'auto',
        }}
      />
    </Paper>

    <Skeleton
      animation={false}
      sx={{ bgcolor: 'secondary.main', width: '80%', maxWidth: 280, mt: 10, opacity: 0.4 }}
    />
    <Skeleton animation={false} sx={{ mt: 2 }} />
    <Skeleton animation={false} />

    <Skeleton animation={false} sx={{ mt: 4 }} />

    <Skeleton animation={false} sx={{ mt: 4 }} />
    <Skeleton animation={false} sx={{ width: '30%' }} />
  </Container>
);

/**
 * IframePage Component
 * Iframe integration page reproducing meteofrance.com environment
 * Allows Climadiag integration in Météo France website with bidirectional communication
 * @returns {JSX.Element} Complete iframe page
 */
const IframePage = () => {
  // Initialize iframe communication
  useInteractiveIframeEffect();

  return (
    <Box
      component="main"
      sx={{
        pt: '180px', // Espace pour le header fixe
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        borderBottom: '50px solid #145e9a', // Bordure Météo France
      }}
    >
      {/* Header Météo France simulé */}
      <MeteoFranceHeader />

      {/* Zone de contenu principal */}
      <Box
        sx={{
          flex: 1,
          background: '#eeeeee',
          py: '35px',
        }}
      >
        {/* Climadiag iframe */}
        <ClimadiagIframe />

        {/* Simulated additional content */}
        <AdditionalContent />
      </Box>
    </Box>
  );
};

export default IframePage;
